import { useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import axiosInstance from "../axios-instance";
import { notification } from "../utils/utility";
import {
  ACCOUNT_SETUP_CREATE_EVENT_MESSAGE,
  ACCOUNT_SETUP_PATH,
  parseAccountSetupComplete,
  resolveAccountSetupBusinessId,
} from "../utils/accountSetup";
import {
  EVENT_CREATE_LIST_PATH,
  isEventCreateListPath,
  requestQuickSetupPulse,
} from "../utils/quickSetupPulse";

/**
 * Block event-create pages until the business finishes account setup.
 * bounceToList: stay on / send back to the event-type list and pulse Quick setup.
 */
const useAccountSetupRedirect = (condition = true, options = {}) => {
  const { bounceToList = false } = options;
  const navigate = useNavigate();
  const notified = useRef(false);

  useEffect(() => {
    if (!condition) {
      return;
    }

    const businessId = resolveAccountSetupBusinessId();
    if (!businessId) {
      return;
    }

    let cancelled = false;

    const sendToSetup = () => {
      if (!notified.current) {
        notified.current = true;
        notification.warning(
          ACCOUNT_SETUP_CREATE_EVENT_MESSAGE,
          "Account setup required"
        );
      }
      navigate(ACCOUNT_SETUP_PATH, { replace: true });
    };

    const sendToListWithPulse = () => {
      if (!notified.current) {
        notified.current = true;
        requestQuickSetupPulse();
      }
      if (!isEventCreateListPath(window.location.pathname)) {
        navigate(EVENT_CREATE_LIST_PATH, { replace: true });
      }
    };

    axiosInstance
      .get(`admin/account-setup-status/${businessId}`)
      .then((response) => {
        if (cancelled) return;
        if (parseAccountSetupComplete(response.data)) {
          return;
        }
        if (bounceToList) {
          sendToListWithPulse();
          return;
        }
        sendToSetup();
      })
      .catch(() => {
        if (cancelled) return;
        if (bounceToList) {
          sendToListWithPulse();
          return;
        }
        sendToSetup();
      });

    return () => {
      cancelled = true;
    };
  }, [bounceToList, condition, navigate]);
};

export default useAccountSetupRedirect;
